import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import httpService from "../../../../services/httpServices";
import toast from "react-hot-toast";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, Copy, Download, Check, Link2, QrCode, BarChart3 } from "lucide-react";
import SocialShareButtons from "./SocialShareButtons";

/**
 * Share link page — path-based slug, social sharing, QR code, optional UTM builder.
 *
 * UTM tracking is opt-in: when enabled, the user can add Google Analytics
 * utm_source / utm_medium / utm_campaign / utm_term / utm_content values
 * which get appended to the share link, the QR code, and the social
 * sharing buttons.
 */
const AffiliateLinks = ({ affiliate }) => {
  const [linkData, setLinkData] = useState(null);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  // UTM state
  const [utmEnabled, setUtmEnabled] = useState(false);
  const [utm, setUtm] = useState({
    source: "",
    medium: "",
    campaign: "",
    term: "",
    content: "",
  });

  useEffect(() => {
    httpService
      .get("/affiliate/links")
      .then((res) => setLinkData(res.data))
      .catch(() => {});
  }, []);

  const baseLink = linkData?.link || "";
  const slug = linkData?.shareSlug || affiliate?.shareSlug || "";

  // Compose the final link with UTM params if enabled and any field is filled
  const link = useMemo(() => {
    if (!baseLink) return "";
    if (!utmEnabled) return baseLink;
    const params = new URLSearchParams();
    if (utm.source.trim()) params.set("utm_source", utm.source.trim());
    if (utm.medium.trim()) params.set("utm_medium", utm.medium.trim());
    if (utm.campaign.trim()) params.set("utm_campaign", utm.campaign.trim());
    if (utm.term.trim()) params.set("utm_term", utm.term.trim());
    if (utm.content.trim()) params.set("utm_content", utm.content.trim());
    const qs = params.toString();
    return qs ? `${baseLink}?${qs}` : baseLink;
  }, [baseLink, utmEnabled, utm]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `rlink-referral-qr-${slug}.png`;
    a.click();
  };

  const updateUtm = (key, value) => setUtm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-8 p-3 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/dashboard/affiliate" className="text-gray-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h2 className="text-2xl font-bold text-white">Share Link</h2>
          <p className="text-gray-400 text-sm">Your unique referral link</p>
        </div>
      </div>

      {/* Link card */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Link2 className="w-5 h-5 text-[#6a1fbf]" />
          Your Link
        </h3>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 bg-white/5 rounded-lg px-4 py-3 border border-white/10 min-w-0">
            <p className="text-[#00c853] font-medium text-sm break-all">{link || "—"}</p>
          </div>
          <button
            onClick={copyToClipboard}
            disabled={!link}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-50 text-white px-5 py-3 rounded-lg font-bold transition-opacity"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>

        <div className="flex items-center gap-4 pt-2 border-t border-white/10">
          <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Share to</span>
          <SocialShareButtons url={link} />
        </div>

        <div className="text-xs text-gray-500">
          Slug: <span className="font-mono text-gray-400">{slug}</span>
        </div>
      </div>

      {/* UTM Builder */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6">
        <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#6a1fbf]/20 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5 text-[#6a1fbf]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Google Analytics UTM Codes</h3>
              <p className="text-gray-400 text-sm">
                Optional — track which campaigns drive your referrals in Google Analytics.
              </p>
            </div>
          </div>
          <ToggleSwitch checked={utmEnabled} onChange={setUtmEnabled} />
        </div>

        {utmEnabled && (
          <div className="space-y-4 pt-4 border-t border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UtmField
                label="Source"
                paramName="utm_source"
                value={utm.source}
                onChange={(v) => updateUtm("source", v)}
                placeholder="e.g. facebook, newsletter, twitter"
                hint="Where the traffic came from"
              />
              <UtmField
                label="Medium"
                paramName="utm_medium"
                value={utm.medium}
                onChange={(v) => updateUtm("medium", v)}
                placeholder="e.g. social, email, cpc"
                hint="Marketing medium"
              />
              <UtmField
                label="Campaign"
                paramName="utm_campaign"
                value={utm.campaign}
                onChange={(v) => updateUtm("campaign", v)}
                placeholder="e.g. spring_launch, black_friday"
                hint="Campaign name"
              />
              <UtmField
                label="Term"
                paramName="utm_term"
                value={utm.term}
                onChange={(v) => updateUtm("term", v)}
                placeholder="e.g. video+conferencing"
                hint="Paid keywords (optional)"
              />
              <div className="md:col-span-2">
                <UtmField
                  label="Content"
                  paramName="utm_content"
                  value={utm.content}
                  onChange={(v) => updateUtm("content", v)}
                  placeholder="e.g. header_button, sidebar_ad"
                  hint="Differentiate similar links in the same campaign"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-gray-500">
                Spaces become <span className="font-mono text-gray-400">+</span> automatically.
              </p>
              <button
                onClick={() => setUtm({ source: "", medium: "", campaign: "", term: "", content: "" })}
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {/* QR */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <QrCode className="w-5 h-5 text-[#6a1fbf]" />
          QR Code
        </h3>
        <div className="flex flex-col items-center gap-4">
          <div ref={qrRef} className="bg-white p-4 rounded-xl">
            <QRCodeCanvas value={link || "https://r-link.com"} size={200} level="M" />
          </div>
          <button
            onClick={downloadQR}
            disabled={!link}
            className="flex items-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg font-bold transition-opacity"
          >
            <Download className="w-4 h-4" /> Download QR Code
          </button>
          {utmEnabled && (
            <p className="text-xs text-gray-500 text-center max-w-xs">
              The QR code encodes your link <em>with</em> the UTM parameters above.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const ToggleSwitch = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      checked ? "bg-gradient-to-r from-[#6a1fbf] to-[#00c853]" : "bg-white/10"
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const UtmField = ({ label, paramName, value, onChange, placeholder, hint }) => (
  <div className="space-y-1.5">
    <div className="flex items-baseline justify-between">
      <label className="text-sm font-medium text-gray-300">{label}</label>
      <span className="text-[10px] text-gray-500 font-mono">{paramName}</span>
    </div>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-500 rounded-lg px-4 py-2.5 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors text-sm"
    />
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);

export default AffiliateLinks;
